import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faUsers, faEdit, faTrash, faSave, faTimes, faSpinner, faSearch,
  faUser, faEnvelope, faCrown, faCalendar, faChartBar, faKey, faArrowLeft
} from '@fortawesome/free-solid-svg-icons';
import api from '../config/api';

interface User {
  id: number;
  email: string;
  full_name: string | null;
  avatar_url: string | null;
  is_admin: boolean;
  social_provider: string | null;
  created_at: string;
  updated_at: string;
  active_subscriptions?: number;
  exam_attempts?: number;
}

const AdminUsers: React.FC = () => {
  const [users, setUsers] = useState<User[]>([]);
  const [loading, setLoading] = useState(true);
  const [editingId, setEditingId] = useState<number | null>(null);
  const [saving, setSaving] = useState(false);
  const [deleting, setDeleting] = useState<number | null>(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [sortBy, setSortBy] = useState('created_at');
  const [sortOrder, setSortOrder] = useState<'ASC' | 'DESC'>('DESC');

  // Form state
  const [formData, setFormData] = useState({
    email: '',
    full_name: '',
    is_admin: false,
    password: ''
  });

  useEffect(() => {
    fetchUsers();
  }, [page, searchQuery, sortBy, sortOrder]);

  const fetchUsers = async () => {
    try {
      setLoading(true);
      const response = await api.get('/users', {
        params: {
          page,
          limit: 50,
          search: searchQuery,
          sortBy,
          sortOrder
        }
      });
      setUsers(response.data.users || []);
      setTotalPages(response.data.pagination?.totalPages || 1);
    } catch (error: any) {
      console.error('Failed to fetch users:', error);
      const errorMessage = error.response?.data?.error || 'Failed to load users';
      alert(`${errorMessage}. ${error.response?.status === 403 ? 'You may not have admin access.' : ''}`);
    } finally {
      setLoading(false);
    }
  };

  const handleEdit = (user: User) => {
    setFormData({
      email: user.email,
      full_name: user.full_name || '',
      is_admin: user.is_admin || false,
      password: ''
    });
    setEditingId(user.id);
  };

  const handleCancel = () => {
    setEditingId(null);
    setFormData({
      email: '',
      full_name: '',
      is_admin: false,
      password: ''
    });
  };

  const handleSave = async () => {
    if (!editingId) return;

    try {
      setSaving(true);
      await api.put(`/users/${editingId}`, formData);
      await fetchUsers();
      handleCancel();
    } catch (error: any) {
      console.error('Failed to update user:', error);
      alert(error.response?.data?.error || 'Failed to update user');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (userId: number) => {
    if (!window.confirm('Are you sure you want to delete this user? This action cannot be undone.')) {
      return;
    }

    try {
      setDeleting(userId);
      await api.delete(`/users/${userId}`);
      await fetchUsers();
    } catch (error: any) {
      console.error('Failed to delete user:', error);
      alert(error.response?.data?.error || 'Failed to delete user');
    } finally {
      setDeleting(null);
    }
  };

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault();
    setPage(1);
    fetchUsers();
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const handleSort = (column: string) => {
    if (sortBy === column) {
      setSortOrder(sortOrder === 'ASC' ? 'DESC' : 'ASC');
    } else {
      setSortBy(column);
      setSortOrder('DESC');
    }
  };

  if (loading && users.length === 0) {
    return (
      <div className="gp-page flex items-center justify-center py-12">
        <div className="gp-spinner" aria-hidden="true" />
      </div>
    );
  }

  return (
    <div className="gp-page py-6">
      <div className="gp-container">
        <div className="gp-page-header">
          <Link to="/admin/certifications" className="gp-back-link mb-3">
            <FontAwesomeIcon icon={faArrowLeft} aria-hidden="true" />
            Back to Admin Panel
          </Link>
          
          <div className="flex items-center gap-2 mb-1">
            <span className="gp-icon-tile">
              <FontAwesomeIcon icon={faUsers} aria-hidden="true" />
            </span>
            <h1 className="gp-page-title">User Management</h1>
          </div>
          <p className="gp-page-subtitle">Manage users, permissions, and access</p>
        </div>

        <div className="gp-card mb-4">
          <form onSubmit={handleSearch} className="flex flex-col sm:flex-row gap-3">
            <div className="flex-1 relative">
              <FontAwesomeIcon 
                icon={faSearch} 
                className="absolute left-3 top-1/2 -translate-y-1/2 text-sm gp-text-muted"
                aria-hidden="true"
              />
              <input
                type="text"
                placeholder="Search by email or name..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="gp-search-input"
              />
            </div>
            <button type="submit" className="gp-btn-primary">
              Search
            </button>
          </form>
        </div>

        <div className="gp-card p-0 overflow-hidden">
          <div className="gp-table-wrap border-0 rounded-none">
            <table>
              <thead>
                <tr>
                  <th 
                    className="cursor-pointer"
                    onClick={() => handleSort('id')}
                  >
                    ID {sortBy === 'id' && (sortOrder === 'ASC' ? '↑' : '↓')}
                  </th>
                  <th 
                    className="cursor-pointer"
                    onClick={() => handleSort('email')}
                  >
                    Email {sortBy === 'email' && (sortOrder === 'ASC' ? '↑' : '↓')}
                  </th>
                  <th 
                    className="cursor-pointer"
                    onClick={() => handleSort('full_name')}
                  >
                    Name {sortBy === 'full_name' && (sortOrder === 'ASC' ? '↑' : '↓')}
                  </th>
                  <th>Role</th>
                  <th>Stats</th>
                  <th 
                    className="cursor-pointer"
                    onClick={() => handleSort('created_at')}
                  >
                    Created {sortBy === 'created_at' && (sortOrder === 'ASC' ? '↑' : '↓')}
                  </th>
                  <th className="!text-right">Actions</th>
                </tr>
              </thead>
              <tbody>
                {users.map((user) => (
                  <tr key={user.id}>
                    {editingId === user.id ? (
                      <>
                        <td className="text-sm">{user.id}</td>
                        <td>
                          <input
                            type="email"
                            value={formData.email}
                            onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                            className="input-field py-1.5"
                          />
                        </td>
                        <td>
                          <input
                            type="text"
                            value={formData.full_name}
                            onChange={(e) => setFormData({ ...formData, full_name: e.target.value })}
                            className="input-field py-1.5"
                          />
                        </td>
                        <td>
                          <label className="flex items-center gap-2">
                            <input
                              type="checkbox"
                              checked={formData.is_admin}
                              onChange={(e) => setFormData({ ...formData, is_admin: e.target.checked })}
                            />
                            <span className="text-sm gp-text-muted">Admin</span>
                          </label>
                        </td>
                        <td className="text-sm gp-text-muted">
                          {user.active_subscriptions || 0} subs • {user.exam_attempts || 0} attempts
                        </td>
                        <td className="text-sm gp-text-muted">
                          {formatDate(user.created_at)}
                        </td>
                        <td className="!text-right">
                          <div className="flex items-center justify-end gap-1">
                            <button
                              type="button"
                              onClick={handleSave}
                              disabled={saving}
                              className="p-2 rounded-md transition-colors"
                              style={{ color: 'var(--gp-success)' }}
                              title="Save"
                            >
                              <FontAwesomeIcon icon={saving ? faSpinner : faSave} className={saving ? 'animate-spin' : ''} />
                            </button>
                            <button
                              type="button"
                              onClick={handleCancel}
                              className="p-2 rounded-md gp-text-muted"
                              title="Cancel"
                            >
                              <FontAwesomeIcon icon={faTimes} />
                            </button>
                          </div>
                        </td>
                      </>
                    ) : (
                      <>
                        <td className="text-sm">{user.id}</td>
                        <td>
                          <div className="flex items-center gap-2">
                            <FontAwesomeIcon icon={faEnvelope} className="gp-text-muted text-xs" aria-hidden="true" />
                            <span className="text-sm">{user.email}</span>
                          </div>
                        </td>
                        <td>
                          <div className="flex items-center gap-2">
                            <FontAwesomeIcon icon={faUser} className="gp-text-muted text-xs" aria-hidden="true" />
                            <span className="text-sm">
                              {user.full_name || 'N/A'}
                            </span>
                          </div>
                        </td>
                        <td>
                          {user.is_admin ? (
                            <span className="gp-badge-brand gp-badge">
                              <FontAwesomeIcon icon={faCrown} aria-hidden="true" />
                              Admin
                            </span>
                          ) : (
                            <span className="gp-badge" style={{ background: 'var(--gp-surface-muted)', color: 'var(--gp-ink-muted)' }}>
                              User
                            </span>
                          )}
                          {user.social_provider && (
                            <span className="ml-2 text-xs gp-text-muted">
                              ({user.social_provider})
                            </span>
                          )}
                        </td>
                        <td>
                          <div className="flex items-center gap-3 text-xs gp-text-muted">
                            <div className="flex items-center gap-1">
                              <FontAwesomeIcon icon={faChartBar} aria-hidden="true" />
                              <span>{user.active_subscriptions || 0} subs</span>
                            </div>
                            <div className="flex items-center gap-1">
                              <FontAwesomeIcon icon={faChartBar} aria-hidden="true" />
                              <span>{user.exam_attempts || 0} attempts</span>
                            </div>
                          </div>
                        </td>
                        <td className="text-sm gp-text-muted">
                          <div className="flex items-center gap-1">
                            <FontAwesomeIcon icon={faCalendar} className="text-xs" aria-hidden="true" />
                            <span>{formatDate(user.created_at)}</span>
                          </div>
                        </td>
                        <td className="!text-right">
                          <div className="flex items-center justify-end gap-1">
                            <button
                              type="button"
                              onClick={() => handleEdit(user)}
                              className="p-2 rounded-md"
                              style={{ color: 'var(--gp-brand)' }}
                              title="Edit"
                            >
                              <FontAwesomeIcon icon={faEdit} />
                            </button>
                            <button
                              type="button"
                              onClick={() => handleDelete(user.id)}
                              disabled={deleting === user.id}
                              className="p-2 rounded-md text-red-600 disabled:opacity-50"
                              title="Delete"
                            >
                              <FontAwesomeIcon icon={deleting === user.id ? faSpinner : faTrash} className={deleting === user.id ? 'animate-spin' : ''} />
                            </button>
                          </div>
                        </td>
                      </>
                    )}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {totalPages > 1 && (
            <div
              className="px-3 py-2.5 flex items-center justify-between"
              style={{ borderTop: '1px solid var(--gp-border)', background: 'var(--gp-surface-muted)' }}
            >
              <div className="text-sm gp-text-muted">
                Page {page} of {totalPages}
              </div>
              <div className="flex gap-2">
                <button
                  type="button"
                  onClick={() => setPage(p => Math.max(1, p - 1))}
                  disabled={page === 1}
                  className="gp-btn-ghost text-xs py-1 px-2.5 disabled:opacity-50"
                >
                  Previous
                </button>
                <button
                  type="button"
                  onClick={() => setPage(p => Math.min(totalPages, p + 1))}
                  disabled={page === totalPages}
                  className="gp-btn-ghost text-xs py-1 px-2.5 disabled:opacity-50"
                >
                  Next
                </button>
              </div>
            </div>
          )}
        </div>

        {editingId && (
          <div className="mt-4 gp-soft-panel p-4">
            <div className="flex items-start gap-3">
              <FontAwesomeIcon icon={faKey} style={{ color: 'var(--gp-brand)' }} className="mt-0.5" aria-hidden="true" />
              <div className="text-sm" style={{ color: 'var(--gp-ink)' }}>
                <strong>Password Reset:</strong> Leave password field empty to keep current password. 
                Enter a new password to reset it.
              </div>
            </div>
            <div className="mt-3">
              <input
                type="password"
                placeholder="New password (leave empty to keep current)"
                value={formData.password}
                onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                className="input-field"
              />
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default AdminUsers;
